import axios from "axios";
import { CreateAccountError } from "@/Apis/CreateAccountError";
import { ResetPasswordError } from "@/Apis/ResetPasswordError";
import type { AcceptInvitationRequest } from "@freightsimple/generated-dashboard-openapi-client/models/AcceptInvitationRequest";
import type { CreateAccountRequest } from "@freightsimple/generated-dashboard-openapi-client/models/CreateAccountRequest";
import type { CreateAccountResponse } from "@freightsimple/generated-dashboard-openapi-client/models/CreateAccountResponse";
import type { LookUpEmailResponse } from "@freightsimple/generated-dashboard-openapi-client/models/LookUpEmailResponse";
import type { ResetPasswordRequest } from "@freightsimple/generated-dashboard-openapi-client/models/ResetPasswordRequest";
import type { SharePrefilledInfoRequest } from "@freightsimple/generated-dashboard-openapi-client/models/SharePrefilledInfoRequest";

export class UsersApi2 {
  baseUrl: string;
  accessToken: string;

  constructor(baseUrl: string, accessToken: string) {
    this.baseUrl = baseUrl;
    this.accessToken = accessToken;
  }

  async postUsersCreateAccount(
    request: CreateAccountRequest,
  ): Promise<CreateAccountResponse> {
    const options = {
      headers: { Authorization: `Bearer ${this.accessToken}` },
      validateStatus: () => true,
    };

    const response = await axios.post(
      `${this.baseUrl}/users/create-account`,
      request,
      options,
    );

    if (response.status === 200) {
      return response.data as CreateAccountResponse;
    }

    if (response.status === 400) {
      throw new CreateAccountError(response.data);
    }

    throw new Error(`Unexpected status ${response.status}`);
  }

  async postUsersResetPassword(request: ResetPasswordRequest): Promise<void> {
    const options = {
      headers: { Authorization: `Bearer ${this.accessToken}` },
      validateStatus: () => true,
    };

    const response = await axios.post(
      `${this.baseUrl}/users/reset-password`,
      request,
      options,
    );

    if (response.status === 200) {
      return;
    }

    if (response.status === 400) {
      throw new ResetPasswordError(response.data);
    }
  }

  async postUsersSharePrefilledInfo(
    request: SharePrefilledInfoRequest,
  ): Promise<void> {
    const options = {
      headers: { Authorization: `Bearer ${this.accessToken}` },
      validateStatus: () => true,
    };

    const response = await axios.post(
      `${this.baseUrl}/users/share-prefilled-info`,
      request,
      options,
    );

    if (response.status === 200) {
      return;
    }

    if (response.status === 400) {
      throw Error("Something went wrong");
    }
  }

  async postUsersAcceptInvitation(
    request: AcceptInvitationRequest,
  ): Promise<void> {
    const options = {
      headers: { Authorization: `Bearer ${this.accessToken}` },
      validateStatus: () => true,
    };

    const response = await axios.post(
      `${this.baseUrl}/users/accept-invitation`,
      request,
      options,
    );

    if (response.status === 200) {
      return;
    }

    if (response.status === 400) {
      throw new CreateAccountError(response.data);
    }
  }

  async isEmailUsed(email: string): Promise<boolean | undefined> {
    try {
      const response = (
        await axios.get(
          `${this.baseUrl}/users/look-up-email?email=${encodeURIComponent(email)}`,
        )
      ).data as LookUpEmailResponse;
      return response.isUsed;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }
}
